import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent } from '@angular/material/dialog';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { AdminUIHelpers } from 'src/app/common/helpers';
import * as _ from 'lodash';
import { UntypedFormBuilder, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IPrepLocationHoursOverride } from 'src/app/common/models/prep-location-hours-override';
import { PrepLocationsService } from 'src/app/services/prep-locations.service';
import { ToastrService } from 'ngx-toastr';
import { IMultiStoreSelect } from 'src/app/common/models/multiStoreSelect';
import { FormHelpers } from 'src/app/common/formHelpers';
import { MultiPrepSelect } from 'src/app/common/models/multiPrepSelect';
import { FacilityManager } from 'src/app/managers/facility.manager';
import { IBulkHoursOverride } from 'src/app/common/models/bulkhoursoverride';
import { interval, of } from 'rxjs';
import { switchMap, takeWhile, catchError } from 'rxjs/operators';
import { StatusService } from 'src/app/services/status.service';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatStepper, MatStep, MatStepLabel, MatStepperNext, MatStepperPrevious } from '@angular/material/stepper';
import { MatFormField, MatLabel, MatInput, MatHint, MatSuffix } from '@angular/material/input';
import { MatDatepickerInput, MatDatepickerToggle, MatDatepicker } from '@angular/material/datepicker';
import { MatSelect, MatOption } from '@angular/material/select';

import { MatCheckbox } from '@angular/material/checkbox';
import { MatButton } from '@angular/material/button';
import { MultiPrepChecklist } from '../../multi-prep-checklist/multi-prep-checklist.component';
import { MultiStoreDropdown } from '../../multi-store-dropdown/multi-store-dropdown.component';
import { TimeOnlyPipe } from '@pipes/time-date-pipe';

interface HoursExceptionDialogData {
  prepLocation: { organizationId: number; prepLocationTypeName: string };
}

@Component({
  selector: 'app-hours-exception-schedule',
  templateUrl: './hours-exception-schedule.component.html',
  styleUrls: ['./hours-exception-schedule.component.scss'],
  providers: [
    // `MomentDateAdapter` and `MAT_MOMENT_DATE_FORMATS` can be automatically provided by importing
    // `MatMomentDateModule` in your applications root module. We provide it at the component level
    // here, due to limitations of our example generation script.
    { provide: DateAdapter, useClass: MomentDateAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
  ],
  imports: [
    CdkScrollable,
    MatDialogContent,
    MatStepper,
    MatStep,
    FormsModule,
    ReactiveFormsModule,
    MatStepLabel,
    MatFormField,
    MatLabel,
    MatInput,
    MatDatepickerInput,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatSelect,
    MatOption,
    MatCheckbox,
    MatButton,
    MatStepperNext,
    MultiPrepChecklist,
    MatStepperPrevious,
    MultiStoreDropdown,
    TimeOnlyPipe,
  ],
})
export class HoursExceptionScheduleComponent {
  public hoursOverrideForm = this.fb.group({
    date: [null, Validators.required],
    openIndex: [''],
    closeIndex: [''],
    closedForDay: [false],
    isMultiStore: [false],
    isMultiPrep: [false],
  });

  public multiStoreForm = this.fb.group({
    facilities: ['', Validators.required],
  });

  public multiPrepForm = this.fb.group({
    prepLocations: ['', Validators.required],
  });

  public facilities: IMultiStoreSelect[] = [];
  public prepLocations: MultiPrepSelect[] = [];
  public currentFacilityId: string;

  public timeRange = AdminUIHelpers.getTimeRange();
  public changedFields: string[] = [];

  private readonly organizationId: number;
  public prepLocationTypeName: string;
  public isFormValid = false;

  constructor(
    public prepService: PrepLocationsService,
    public dialogRef: MatDialogRef<HoursExceptionScheduleComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: HoursExceptionDialogData,
    private fb: UntypedFormBuilder,
    private toastr: ToastrService,
    private statusService: StatusService,
    private _facilityManager: FacilityManager,
  ) {
    this.organizationId = dialogData.prepLocation.organizationId;
    this.prepLocationTypeName = dialogData.prepLocation.prepLocationTypeName;
    this.currentFacilityId = this._facilityManager.getCurrentFacility();

    this.getChangedFields();

    this.hoursOverrideForm.valueChanges.subscribe(() => {
      this.getChangedFields();
      this.isFormValid = this.openBeforeCloseValidator() && this.hasAnyChange();
    });
  }

  public onNoClick(): void {
    this.dialogRef.close([]);
  }

  public clearDate(): void {
    this.hoursOverrideForm.patchValue({ date: null });
  }

  public facilityValidation(): void {
    this.multiStoreForm.get('facilities')?.setValue(FormHelpers.areOptionsChecked(this.facilities));
  }

  public prepLocationValidation(): void {
    this.multiPrepForm.get('prepLocations')?.setValue(FormHelpers.areOptionsChecked(this.prepLocations));
  }

  public isMultiAny(): boolean {
    return this.hoursOverrideForm.value['isMultiStore'] || this.hoursOverrideForm.value['isMultiPrep'];
  }

  public getChangedFields(): void {
    const form = this.hoursOverrideForm.value;
    const changes: string[] = [];

    if (form.date !== null) {
      changes.push(`Hours Override Information, Date for Hours Override: ${form.date.format('M/D/YYYY')}`);
    }

    if (form.openIndex !== '') {
      const openTime = this.timeRange?.[form.openIndex]?.time;
      if (openTime) {
        changes.push(`Hours Override Information, Open: ${openTime.format('h:mm A')}`);
      }
    }

    if (form.closeIndex !== '') {
      const closeTime = this.timeRange?.[form.closeIndex]?.time;
      if (closeTime) {
        changes.push(`Hours Override Information, Close: ${closeTime.format('h:mm A')}`);
      }
    }

    if (form.closedForDay === true) {
      changes.push(`Hours Override Information, Closed for day`);
    }

    this.changedFields = changes;
  }

  public openBeforeCloseValidator(): boolean {
    const closedForDay = this.hoursOverrideForm.value['closedForDay'];
    if (closedForDay) return true;

    const rawOpenIndex = this.hoursOverrideForm.value['openIndex'];
    const rawCloseIndex = this.hoursOverrideForm.value['closeIndex'];

    if (rawOpenIndex === '' || rawCloseIndex === '') return true;

    return Number(rawOpenIndex) < Number(rawCloseIndex);
  }

  public hasAnyChange(): boolean {
    const form = this.hoursOverrideForm.value;
    return form.openIndex !== '' || form.closeIndex !== '' || form.closedForDay === true;
  }

  public saveHoursOverride(): void {
    const bulkHoursOverridesDTO: IBulkHoursOverride[] = [];

    this.statusService.forceLoader = true;

    const selectedFacilityIds = AdminUIHelpers.getSelectedFacilityIds(this.currentFacilityId, this.facilities);
    const selectedPrepLocations = this.prepService.getSelectedPrepLocations(
      this.prepLocations,
      this.prepLocationTypeName,
    );

    const openTime =
      this.hoursOverrideForm.value['openIndex'] !== ''
        ? this.timeRange[this.hoursOverrideForm.value['openIndex']].time.format('HH:mm:ss')
        : null;
    const closeTime =
      this.hoursOverrideForm.value['closeIndex'] !== ''
        ? this.timeRange[this.hoursOverrideForm.value['closeIndex']].time.format('HH:mm:ss')
        : null;

    const overridesForFacility: IPrepLocationHoursOverride[] = selectedPrepLocations.map((prepLocation: string) => {
      return {
        organizationId: this.organizationId,
        facilityId: this.currentFacilityId,
        prepLocationTypeName: prepLocation,
        date: this.hoursOverrideForm.value['date'].format(),
        isClosedForDay: this.hoursOverrideForm.value['closedForDay'] || false,
        openTime: openTime,
        closeTime: closeTime,
      } as IPrepLocationHoursOverride;
    });

    bulkHoursOverridesDTO.push({
      organizationId: this.organizationId,
      facilities: selectedFacilityIds,
      prepLocationTypeName: selectedPrepLocations,
      isClosedForDay: this.hoursOverrideForm.value['closedForDay'] || false,
      openTime: openTime,
      closeTime: closeTime,
      date: this.hoursOverrideForm.value['date'].format(),
    });

    this.prepService.upsertBulkHoursOverrides(bulkHoursOverridesDTO).subscribe({
      next: (res: any) => {
        if (res && res.InstanceId && res.Code) {
          interval(2000)
            .pipe(
              switchMap(() => this.prepService.getDurableStatus(res.InstanceId, res.Code)),
              takeWhile((response: any) => {
                if (response.status === 202) {
                  this.toastr.info('Processing, please wait...');
                  return true;
                }
                if (response.status === 200 || response.status === 500) return false;
                return false;
              }, true),
              catchError((err) => {
                this.statusService.forceLoader = false;
                this.toastr.error('Error checking process status');
                console.error(err);
                return of(null);
              }),
            )
            .subscribe((response: any) => {
              if (!response) return;
              if (response.status === 200) {
                this.statusService.forceLoader = false;
                this.toastr.success('Hours Override process completed!');
                this.dialogRef.close(overridesForFacility);
              } else if (response.status === 500) {
                this.statusService.forceLoader = false;
                this.toastr.error('Hours Override process failed.');
              }
            });
        } else {
          this.statusService.forceLoader = false;
          this.toastr.error('Error creating Hours Override');
        }
      },
      error: (e) => {
        this.statusService.forceLoader = false;
        this.toastr.error('Error when creating Hours Override');
      },
    });
  }
}
